/**
 * Standalone promote admin: Cognito Hosted UI (PKCE) + Promote API.
 * Gated to SuperAdmins in the existing Comita Cognito pool.
 */

export interface PromoteAdminConfig {
  apiUrl: string;
  userPoolId: string;
  clientId: string;
  cognitoDomain: string;
  redirectUri: string;
}

interface TokenBundle {
  accessToken: string;
  idToken: string;
  refreshToken: string;
  expiresAt: number;
}

interface WorkflowRunView {
  id: number;
  status: string;
  conclusion: string | null;
  url: string;
  inProgress: boolean;
  startedAt: string | null;
  typicalDurationSeconds: number | null;
  estimatedRemainingSeconds: number | null;
}

export interface PromoteStatus {
  devSha: string;
  prodSha: string;
  hasPendingChanges: boolean;
  canFastForward: boolean;
  compareStatus: string;
  message: string;
  promoteRun: WorkflowRunView | null;
  deployRun: WorkflowRunView | null;
  pipelineBusy: boolean;
  canPromote: boolean;
  /** Previous production release, when one exists and is an ancestor of prod. */
  previousProdSha?: string;
  previousProdTag?: string;
  canRollback?: boolean;
  pipelineEtaSeconds: number | null;
  /** True when the latest promote/deploy run ended in a failure conclusion. */
  pipelineFailed: boolean;
  /** The terminal-failed run (promote preferred over deploy), if any. */
  failedRun: WorkflowRunView | null;
}

export type PipelinePhase = 'idle' | 'awaiting' | 'running' | 'succeeded' | 'failed' | 'stalled';

export interface PipelineView {
  phase: PipelinePhase;
  /** Actions run URL to link to for failed/running/stalled phases. */
  runUrl: string | null;
}

const TOKEN_STORAGE_KEY = 'comita.promote.tokens';
const PKCE_VERIFIER_KEY = 'comita.promote.pkce';
const MONITORING_STORAGE_KEY = 'comita.promote.monitoring';
const DISPATCHED_AT_KEY = 'comita.promote.dispatchedAt';

// A promote+deploy pipeline that has not reached a terminal state after this
// long is treated as "stalled" so the operator gets a nudge to check Actions
// instead of watching "starting…" forever.
const MONITOR_STALL_MS = 6 * 60 * 1000;
// Tolerance when matching a failed run to the current attempt — the dispatch
// timestamp is recorded a beat after GitHub queues the run, so allow slack.
const CLOCK_SKEW_MS = 30 * 1000;

function sha256Base64Url(input: string): Promise<string> {
  const data = new TextEncoder().encode(input);
  return crypto.subtle.digest('SHA-256', data).then((hash) => {
    const bytes = new Uint8Array(hash);
    let binary = '';
    bytes.forEach((byte) => {
      binary += String.fromCharCode(byte);
    });
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  });
}

function randomString(length: number): string {
  const bytes = crypto.getRandomValues(new Uint8Array(length));
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

function readTokens(): TokenBundle | null {
  const raw = sessionStorage.getItem(TOKEN_STORAGE_KEY);
  if (raw === null) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as TokenBundle;
    if (typeof parsed.accessToken !== 'string' || typeof parsed.idToken !== 'string') {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function writeTokens(tokens: TokenBundle): void {
  sessionStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(tokens));
}

function clearTokens(): void {
  sessionStorage.removeItem(TOKEN_STORAGE_KEY);
}

function cognitoBaseUrl(cognitoDomain: string): string {
  return cognitoDomain.startsWith('http')
    ? cognitoDomain.replace(/\/$/, '')
    : `https://${cognitoDomain}`;
}

async function beginSignIn(config: PromoteAdminConfig): Promise<void> {
  const verifier = randomString(32);
  sessionStorage.setItem(PKCE_VERIFIER_KEY, verifier);
  const challenge = await sha256Base64Url(verifier);
  const params = new URLSearchParams({
    client_id: config.clientId,
    response_type: 'code',
    scope: 'openid email',
    redirect_uri: config.redirectUri,
    code_challenge_method: 'S256',
    code_challenge: challenge,
  });
  window.location.assign(
    `${cognitoBaseUrl(config.cognitoDomain)}/oauth2/authorize?${params.toString()}`,
  );
}

async function exchangeCode(config: PromoteAdminConfig, code: string): Promise<TokenBundle> {
  const verifier = sessionStorage.getItem(PKCE_VERIFIER_KEY);
  if (verifier === null) {
    throw new Error('Missing PKCE verifier.');
  }
  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: config.clientId,
    code,
    redirect_uri: config.redirectUri,
    code_verifier: verifier,
  });
  const response = await fetch(`${cognitoBaseUrl(config.cognitoDomain)}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  });
  if (!response.ok) {
    throw new Error(`Cognito token exchange failed (${response.status}).`);
  }
  const payload = (await response.json()) as {
    access_token: string;
    id_token: string;
    refresh_token?: string;
    expires_in: number;
  };
  const tokens: TokenBundle = {
    accessToken: payload.access_token,
    idToken: payload.id_token,
    refreshToken: payload.refresh_token ?? '',
    expiresAt: Date.now() + payload.expires_in * 1000,
  };
  writeTokens(tokens);
  sessionStorage.removeItem(PKCE_VERIFIER_KEY);
  return tokens;
}

function apiBase(config: PromoteAdminConfig): string {
  return config.apiUrl.replace(/\/$/, '');
}

async function readApiError(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as { error?: string };
    if (typeof body.error === 'string' && body.error !== '') {
      return body.error;
    }
  } catch {
    // not JSON
  }
  return `Request failed (${response.status}).`;
}

async function apiFetch(
  config: PromoteAdminConfig,
  idToken: string,
  path: string,
  init?: RequestInit,
): Promise<Response> {
  let response: Response;
  try {
    response = await fetch(`${apiBase(config)}${path}`, {
      ...init,
      headers: {
        Authorization: `Bearer ${idToken}`,
        ...(init?.headers ?? {}),
      },
    });
  } catch {
    throw new Error(`Could not reach the promote API at ${config.apiUrl}.`);
  }
  if (response.status === 401 || response.status === 403) {
    clearTokens();
    throw new Error('Session expired or not authorized.');
  }
  return response;
}

async function fetchStatus(config: PromoteAdminConfig, idToken: string): Promise<PromoteStatus> {
  const response = await apiFetch(config, idToken, '/promote/status');
  if (!response.ok) {
    throw new Error(`Status request failed (${response.status}).`);
  }
  const payload = (await response.json()) as PromoteStatus;
  return {
    ...payload,
    canPromote: payload.canPromote === true,
    canRollback: payload.canRollback === true,
    previousProdSha: payload.previousProdSha ?? '',
    previousProdTag: payload.previousProdTag ?? '',
    pipelineFailed: payload.pipelineFailed === true,
    failedRun: payload.failedRun ?? null,
  };
}

/**
 * Decide what the pipeline is doing from a status snapshot plus local monitoring
 * context. Pure so it can be reasoned about (and unit tested) in isolation.
 *
 * A terminal failure only counts as the *current* attempt when the failed run
 * started at/after we dispatched (with skew tolerance); otherwise it is a stale
 * prior failure we should ignore. When `dispatchedAt` is unknown (e.g. a page
 * reload restored monitoring), any reported failure while changes are still
 * pending is treated as the current attempt.
 */
export function derivePipelinePhase(
  status: PromoteStatus,
  monitoring: boolean,
  dispatchedAt: number | null,
  now: number,
): PipelineView {
  if (status.pipelineFailed && status.failedRun !== null) {
    const startedMs =
      status.failedRun.startedAt !== null ? Date.parse(status.failedRun.startedAt) : Number.NaN;
    const isCurrentAttempt =
      dispatchedAt === null || Number.isNaN(startedMs) || startedMs >= dispatchedAt - CLOCK_SKEW_MS;
    if (monitoring && isCurrentAttempt) {
      return { phase: 'failed', runUrl: status.failedRun.url !== '' ? status.failedRun.url : null };
    }
  }

  if (status.pipelineBusy) {
    const runUrl =
      status.promoteRun !== null && status.promoteRun.inProgress
        ? status.promoteRun.url
        : status.deployRun !== null && status.deployRun.inProgress
          ? status.deployRun.url
          : null;
    return { phase: 'running', runUrl: runUrl !== '' ? runUrl : null };
  }

  if (monitoring) {
    if (!status.hasPendingChanges) {
      return { phase: 'succeeded', runUrl: null };
    }
    if (dispatchedAt !== null && now - dispatchedAt > MONITOR_STALL_MS) {
      return { phase: 'stalled', runUrl: null };
    }
    return { phase: 'awaiting', runUrl: null };
  }

  return { phase: 'idle', runUrl: null };
}

async function triggerRollback(config: PromoteAdminConfig, idToken: string): Promise<void> {
  const response = await apiFetch(config, idToken, '/promote/rollback', { method: 'POST' });
  if (response.status === 409) {
    throw new Error('Rollback is not available right now.');
  }
  if (!response.ok && response.status !== 202) {
    throw new Error(await readApiError(response));
  }
}

async function triggerPromote(config: PromoteAdminConfig, idToken: string): Promise<void> {
  const response = await apiFetch(config, idToken, '/promote', { method: 'POST' });
  if (response.status === 409) {
    throw new Error('Promote is not available right now.');
  }
  if (!response.ok && response.status !== 202) {
    throw new Error(await readApiError(response));
  }
}

function formatPipeline(status: PromoteStatus): string {
  if (status.promoteRun !== null && status.promoteRun.inProgress) {
    return `Promote workflow ${status.promoteRun.status}`;
  }
  if (status.deployRun !== null && status.deployRun.inProgress) {
    return `Prod deploy ${status.deployRun.status}`;
  }
  if (status.canPromote) {
    return 'Idle — ready to promote.';
  }
  return status.message;
}

function formatEta(seconds: number | null): string | null {
  if (seconds === null || seconds <= 0) {
    return null;
  }
  if (seconds < 90) {
    return `~${seconds}s remaining`;
  }
  const minutes = Math.ceil(seconds / 60);
  if (minutes === 1) {
    return '~1 min remaining';
  }
  return `~${minutes} min remaining`;
}

function shortSha(sha: string): string {
  return sha.length >= 7 ? sha.slice(0, 7) : sha;
}

export function initPromoteAdmin(config: PromoteAdminConfig): void {
  const signInButton = document.getElementById('promote-sign-in');
  const submitButton = document.getElementById('promote-submit');
  const rollbackButton = document.getElementById('promote-rollback');
  const lead = document.getElementById('promote-status-lead');
  const detail = document.getElementById('promote-status-detail');
  const devSha = document.getElementById('promote-dev-sha');
  const prodSha = document.getElementById('promote-prod-sha');
  const previousSha = document.getElementById('promote-previous-sha');
  const pending = document.getElementById('promote-pending');
  const pipeline = document.getElementById('promote-pipeline');
  const eta = document.getElementById('promote-eta');

  if (
    !(signInButton instanceof HTMLButtonElement) ||
    !(submitButton instanceof HTMLButtonElement) ||
    !(rollbackButton instanceof HTMLButtonElement) ||
    lead === null ||
    detail === null ||
    devSha === null ||
    prodSha === null ||
    previousSha === null ||
    pending === null ||
    pipeline === null ||
    eta === null
  ) {
    return;
  }

  if (
    config.apiUrl === '' ||
    config.clientId === '' ||
    config.userPoolId === '' ||
    config.cognitoDomain === ''
  ) {
    lead.textContent = 'Promote API or Cognito is not configured for this host.';
    submitButton.disabled = true;
    submitButton.setAttribute('aria-disabled', 'true');
    return;
  }

  let pollTimer: number | undefined;
  let promoteAllowed = false;
  let rollbackAllowed = false;
  let rollbackTarget = '';
  let statusLoading = false;
  let actionInFlight = false;
  let monitoringPipeline = sessionStorage.getItem(MONITORING_STORAGE_KEY) === 'true';

  const setMonitoring = (active: boolean): void => {
    monitoringPipeline = active;
    if (active) {
      sessionStorage.setItem(MONITORING_STORAGE_KEY, 'true');
      // Anchor the attempt so a later failed run can be matched to it. Only set
      // once so a page reload during monitoring keeps the original dispatch time.
      if (sessionStorage.getItem(DISPATCHED_AT_KEY) === null) {
        sessionStorage.setItem(DISPATCHED_AT_KEY, String(Date.now()));
      }
      return;
    }
    sessionStorage.removeItem(MONITORING_STORAGE_KEY);
    sessionStorage.removeItem(DISPATCHED_AT_KEY);
  };

  const getDispatchedAt = (): number | null => {
    const raw = sessionStorage.getItem(DISPATCHED_AT_KEY);
    if (raw === null) {
      return null;
    }
    const parsed = Number.parseInt(raw, 10);
    return Number.isNaN(parsed) ? null : parsed;
  };

  const setLead = (text: string, tone: 'default' | 'error' | 'warn' | 'ok'): void => {
    lead.textContent = text;
    lead.classList.remove('status-lead--error', 'status-lead--warn', 'status-lead--ok');
    if (tone === 'error') {
      lead.classList.add('status-lead--error');
    } else if (tone === 'warn') {
      lead.classList.add('status-lead--warn');
    } else if (tone === 'ok') {
      lead.classList.add('status-lead--ok');
    }
  };

  const setPipelineCell = (label: string, runUrl: string | null): void => {
    pipeline.textContent = label;
    if (runUrl === null) {
      return;
    }
    // Build the link via DOM (no innerHTML) so the run URL can't inject markup.
    pipeline.append(document.createTextNode(' — '));
    const anchor = document.createElement('a');
    anchor.className = 'link';
    anchor.href = runUrl;
    anchor.target = '_blank';
    anchor.rel = 'noopener noreferrer';
    anchor.textContent = 'view run';
    pipeline.append(anchor);
  };

  const stopPolling = (): void => {
    if (pollTimer !== undefined) {
      window.clearInterval(pollTimer);
      pollTimer = undefined;
    }
  };

  const startPolling = (): void => {
    if (pollTimer !== undefined) {
      return;
    }
    pollTimer = window.setInterval(() => {
      void refresh();
    }, 10_000);
  };

  const syncPolling = (status: PromoteStatus): void => {
    if (monitoringPipeline || status.pipelineBusy) {
      startPolling();
      return;
    }
    stopPolling();
  };

  const setSubmitEnabled = (promote: boolean, rollback: boolean): void => {
    promoteAllowed = promote;
    rollbackAllowed = rollback;
    const interactive = !statusLoading && !actionInFlight && !monitoringPipeline;
    submitButton.disabled = !(interactive && promote);
    submitButton.setAttribute('aria-disabled', interactive && promote ? 'false' : 'true');
    rollbackButton.disabled = !(interactive && rollback);
    rollbackButton.setAttribute('aria-disabled', interactive && rollback ? 'false' : 'true');
  };

  const syncSignInLabel = (): void => {
    signInButton.textContent = readTokens() !== null ? 'Sign out' : 'Sign in';
  };

  const renderStatus = (status: PromoteStatus, signedIn: boolean): void => {
    detail.hidden = false;
    devSha.textContent = shortSha(status.devSha);
    prodSha.textContent = shortSha(status.prodSha);
    previousSha.textContent =
      status.previousProdSha !== undefined && status.previousProdSha !== ''
        ? `${shortSha(status.previousProdSha)}${status.previousProdTag !== undefined && status.previousProdTag !== '' ? ` (${status.previousProdTag})` : ''}`
        : '—';
    rollbackTarget = status.previousProdSha ?? '';
    pending.textContent = status.hasPendingChanges ? 'Yes' : 'No';
    const etaLabel = formatEta(status.pipelineEtaSeconds);
    eta.textContent = etaLabel ?? '—';

    const view = derivePipelinePhase(status, monitoringPipeline, getDispatchedAt(), Date.now());

    if (!signedIn) {
      setPipelineCell(formatPipeline(status), null);
      setSubmitEnabled(false, false);
      return;
    }

    switch (view.phase) {
      case 'failed': {
        // Terminal failure for this attempt: stop watching, surface the run +
        // that a Slack alert was sent, and re-open the button so it can retry.
        setMonitoring(false);
        setPipelineCell('Failed', view.runUrl);
        setLead(
          'Promotion failed — a Slack alert was sent requesting help. Review the run, then retry once resolved.',
          'error',
        );
        setSubmitEnabled(status.canPromote === true, status.canRollback === true);
        return;
      }
      case 'succeeded': {
        setMonitoring(false);
        setPipelineCell('Production is up to date with dev.', null);
        setLead('Production is up to date with dev.', 'ok');
        setSubmitEnabled(status.canPromote === true, status.canRollback === true);
        return;
      }
      case 'stalled': {
        setPipelineCell('Taking longer than expected', view.runUrl);
        setLead(
          'Still promoting after several minutes — check the Actions run. A Slack alert is sent automatically if it fails.',
          'warn',
        );
        setSubmitEnabled(false, false);
        return;
      }
      case 'running': {
        setPipelineCell(formatPipeline(status), view.runUrl);
        setLead(formatPipeline(status), 'default');
        setSubmitEnabled(false, false);
        return;
      }
      case 'awaiting': {
        setPipelineCell('Promote workflow starting…', null);
        setLead('Promote workflow dispatched. Monitoring pipeline…', 'default');
        setSubmitEnabled(false, false);
        return;
      }
      default: {
        setPipelineCell(formatPipeline(status), null);
        setLead(
          status.canPromote ? 'Ready to promote dev to production.' : status.message,
          'default',
        );
        setSubmitEnabled(status.canPromote === true, status.canRollback === true);
      }
    }
  };

  const refresh = async (): Promise<void> => {
    const tokens = readTokens();
    if (tokens === null) {
      setLead('Sign in to load branch and deployment status.', 'default');
      signInButton.textContent = 'Sign in';
      setSubmitEnabled(false, false);
      detail.hidden = true;
      stopPolling();
      return;
    }

    statusLoading = true;
    setSubmitEnabled(false, false);
    syncSignInLabel();

    try {
      const status = await fetchStatus(config, tokens.idToken);
      statusLoading = false;
      renderStatus(status, true);
      // renderStatus clears monitoring on terminal phases; poll only while active.
      syncPolling(status);
    } catch (error) {
      statusLoading = false;
      setLead(error instanceof Error ? error.message : 'Could not load status.', 'error');
      syncSignInLabel();
      setSubmitEnabled(false, false);
      detail.hidden = true;
      stopPolling();
      setMonitoring(false);
    }
  };

  signInButton.addEventListener('click', () => {
    if (readTokens() !== null) {
      clearTokens();
      setMonitoring(false);
      stopPolling();
      void refresh();
      return;
    }
    void beginSignIn(config);
  });

  submitButton.addEventListener('click', () => {
    const tokens = readTokens();
    if (tokens === null) {
      setLead('Sign in before promoting.', 'default');
      return;
    }
    if (!promoteAllowed) {
      setLead('Promote is not available right now.', 'default');
      return;
    }

    promoteAllowed = false;
    actionInFlight = true;
    setSubmitEnabled(false, false);
    setLead('Dispatching promote workflow…', 'default');
    void triggerPromote(config, tokens.idToken)
      .then(() => {
        setMonitoring(true);
        setLead('Promote workflow dispatched. Monitoring pipeline…', 'default');
        startPolling();
      })
      .catch((error: unknown) => {
        setLead(error instanceof Error ? error.message : 'Promote failed.', 'error');
        setMonitoring(false);
      })
      .finally(() => {
        actionInFlight = false;
        void refresh();
      });
  });

  rollbackButton.addEventListener('click', () => {
    const tokens = readTokens();
    if (tokens === null) {
      setLead('Sign in before rolling back.', 'default');
      return;
    }
    if (!rollbackAllowed || rollbackTarget === '') {
      setLead('Rollback is not available right now.', 'default');
      return;
    }
    const label = shortSha(rollbackTarget);
    const confirmed = window.confirm(
      `Roll production back to ${label}? This redeploys that release. Dev stays where it is, so you can promote again afterward.`,
    );
    if (!confirmed) {
      return;
    }

    actionInFlight = true;
    setSubmitEnabled(false, false);
    setLead('Dispatching rollback…', 'default');
    void triggerRollback(config, tokens.idToken)
      .then(() => {
        setMonitoring(true);
        setLead('Rollback dispatched. Monitoring pipeline…', 'default');
        startPolling();
      })
      .catch((error: unknown) => {
        setLead(error instanceof Error ? error.message : 'Rollback failed.', 'error');
        setMonitoring(false);
      })
      .finally(() => {
        actionInFlight = false;
        void refresh();
      });
  });

  const params = new URLSearchParams(window.location.search);
  const code = params.get('code');
  if (code !== null) {
    lead.textContent = 'Completing sign-in…';
    void exchangeCode(config, code)
      .then(() => {
        window.history.replaceState({}, document.title, config.redirectUri);
        return refresh();
      })
      .catch((error: unknown) => {
        lead.textContent = error instanceof Error ? error.message : 'Sign-in failed.';
      });
    return;
  }

  setSubmitEnabled(false, false);
  if (monitoringPipeline) {
    startPolling();
  }
  void refresh();
}
